/**
 * ============================================================
 * Countdown Timer — countdown-timer.js
 * Shopify 2.0 / Dawn compatible
 * 
 * Features:
 *  • Reads end timestamp from data-end attribute (ms)
 *  • Smooth flip-card animation per digit
 *  • Animated urgency progress bar
 *  • Blink separators
 *  • requestAnimationFrame-based tick (smooth + battery-friendly)
 *  • Handles multiple timers on the same page
 *  • Auto-destroys on expiry; shows expired message
 * ============================================================
 */

(function () {
  'use strict';

  /* ── Helpers ─────────────────────────────────────────────── */
  const pad = (n) => String(Math.floor(n)).padStart(2, '0');

  function calcTimeLeft(endMs) {
    const diff = endMs - Date.now();
    if (diff <= 0) return null;
    return {
      days:    Math.floor(diff / 86400000),
      hours:   Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000)  / 60000),
      seconds: Math.floor((diff % 60000)    / 1000),
      total:   diff,
    };
  }

  /* ── FlipUnit ────────────────────────────────────────────── */
  class FlipUnit {
    constructor(containerEl) {
      this.container = containerEl;
      this.card  = containerEl.querySelector('.ct-flip__card');
      this.front = containerEl.querySelector('.ct-flip__front .ct-flip__num');
      this.back  = containerEl.querySelector('.ct-flip__back  .ct-flip__num');
      this.current = null;
      this.animating = false;
    }

    update(value) {
      const str = pad(value);
      if (str === this.current) return;

      const prev = this.current;
      this.current = str;

      if (prev === null) {
        // First paint — no animation
        this.front.textContent = str;
        this.back.textContent  = str;
        return;
      }

      if (this.animating) {
        // If mid-animation, fast-forward: just set the value
        this.front.textContent = str;
        this.back.textContent  = str;
        return;
      }

      this._animate(prev, str);
    }

    _animate(from, to) {
      this.animating = true;

      // Phase 1 — fold current face away (rotateX 0 → 90)
      this.front.textContent = from;
      this.back.textContent  = to;
      this.back.style.opacity = '0';

      const card = this.card;
      card.classList.remove('ct-animating-in', 'ct-animating-out', 'ct-flash');

      // Trigger reflow
      void card.offsetWidth;
      card.classList.add('ct-animating-out');

      const halfDur = parseFloat(
        getComputedStyle(document.documentElement)
          .getPropertyValue('--ct-flip-dur') || '0.52'
      ) * 1000 / 2;

      const t1 = setTimeout(() => {
        // Mid-flip: swap faces
        this.front.textContent = to;
        this.back.style.opacity = '1';
        card.classList.remove('ct-animating-out');
        void card.offsetWidth;
        card.classList.add('ct-animating-in');

        const t2 = setTimeout(() => {
          card.classList.remove('ct-animating-in');
          card.classList.add('ct-flash');
          this.back.textContent = to;
          this.animating = false;

          const t3 = setTimeout(() => {
            card.classList.remove('ct-flash');
          }, 350);
          this._clearTimers = () => clearTimeout(t3);
        }, halfDur);

        this._clearTimers = () => clearTimeout(t2);
      }, halfDur);

      this._clearTimers = () => clearTimeout(t1);
    }

    destroy() {
      if (this._clearTimers) this._clearTimers();
    }
  }

  /* ── CountdownTimer ──────────────────────────────────────── */
  class CountdownTimer {
    constructor(wrapperEl) {
      this.el          = wrapperEl;
      this.endMs       = parseInt(wrapperEl.dataset.end, 10);
      this.expiredMsg  = wrapperEl.dataset.expiredMsg || 'This offer has ended.';
      this.threshold   = parseInt(wrapperEl.dataset.threshold, 10) || 21600000; // 6 h default
      this.id          = wrapperEl.id;

      this.expiredEl   = wrapperEl.querySelector('[id^="ct-expired-"]');
      this.gridEl      = wrapperEl.querySelector('[id^="ct-grid-"]');
      this.barFillEl   = wrapperEl.querySelector('[id^="ct-bar-fill-"]');

      // Build flip units
      this.units = {
        days:    new FlipUnit(wrapperEl.querySelector('[data-unit="days"]').closest('.ct-flip')),
        hours:   new FlipUnit(wrapperEl.querySelector('[data-unit="hours"]').closest('.ct-flip')),
        minutes: new FlipUnit(wrapperEl.querySelector('[data-unit="minutes"]').closest('.ct-flip')),
        seconds: new FlipUnit(wrapperEl.querySelector('[data-unit="seconds"]').closest('.ct-flip')),
      };

      // Urgency bar — compute total duration (cap at 30 days for visual clarity)
      this.totalDuration = Math.min(this.endMs - Date.now(), 30 * 86400000);

      this._raf = null;
      this._lastSec = -1;

      this._tick = this._tick.bind(this);
      this._start();
    }

    _start() {
      const initial = calcTimeLeft(this.endMs);
      if (!initial) {
        this._expire();
        return;
      }
      this._render(initial);
      this._raf = requestAnimationFrame(this._tick);
    }

    _tick() {
      const t = calcTimeLeft(this.endMs);
      if (!t) {
        this._expire();
        return;
      }

      // Only re-render digits on a new second
      const sec = t.seconds + t.minutes * 60 + t.hours * 3600 + t.days * 86400;
      if (sec !== this._lastSec) {
        this._lastSec = sec;
        this._render(t);
        this._updateBar(t.total);
      }

      this._raf = requestAnimationFrame(this._tick);
    }

    _render(t) {
      this.units.days.update(t.days);
      this.units.hours.update(t.hours);
      this.units.minutes.update(t.minutes);
      this.units.seconds.update(t.seconds);
    }

    _updateBar(remaining) {
      if (!this.barFillEl) return;
      const pct = Math.max(0, Math.min(1, remaining / this.totalDuration));
      this.barFillEl.style.width = (pct * 100).toFixed(2) + '%';

      if (remaining <= this.threshold) {
        this.barFillEl.classList.add('ct-urgent');
      } else {
        this.barFillEl.classList.remove('ct-urgent');
      }
    }

    _expire() {
      if (this._raf) cancelAnimationFrame(this._raf);
      Object.values(this.units).forEach(u => u.destroy());

      if (this.gridEl)    this.gridEl.hidden    = true;
      if (this.expiredEl) this.expiredEl.hidden  = false;
      if (this.barFillEl) {
        this.barFillEl.style.width = '0%';
        this.barFillEl.classList.remove('ct-urgent');
      }
    }

    destroy() {
      if (this._raf) cancelAnimationFrame(this._raf);
      Object.values(this.units).forEach(u => u.destroy());
    }
  }

  /* ── Registry ────────────────────────────────────────────── */
  const instances = new Map();

  function initAll() {
    document.querySelectorAll('.ct-wrapper[data-end]').forEach((el) => {
      if (!instances.has(el.id)) {
        instances.set(el.id, new CountdownTimer(el));
      }
    });
  }

  /* ── Boot ────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ── Theme editor live updates ───────────────────────────── */
  document.addEventListener('shopify:block:load',   () => initAll());
  document.addEventListener('shopify:block:unload', (e) => {
    const id = e.target?.id;
    if (id && instances.has(id)) {
      instances.get(id).destroy();
      instances.delete(id);
    }
  });
  document.addEventListener('shopify:section:load', () => initAll());

})();
